import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link, useLocation, useNavigate } from 'react-router-dom';

const CALENDLY_URL = 'https://calendly.com/jagan-motionx/30min';

const NAV_ITEMS = [
  { label: 'Work', href: '#featured-work' },
  { label: 'Feature Films', href: '#feature-films' },
  { label: 'Originals', href: '#originals' },
  { label: 'Hybrid VFX', href: '#before-after' },
  { label: 'Capabilities', href: '#capabilities' },
  { label: 'Pipeline', href: '#pipeline' },
];

const Navigation = () => {
  const [navSolid, setNavSolid] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const handleScroll = () => {
      const scrollTop = window.scrollY || document.documentElement.scrollTop || 0;
      setNavSolid(scrollTop > 40);
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    handleScroll();
    return () => window.removeEventListener('scroll', handleScroll);
  }, [location.pathname]);

  useEffect(() => {
    document.body.style.overflow = mobileMenuOpen ? 'hidden' : '';
    return () => {
      document.body.style.overflow = '';
    };
  }, [mobileMenuOpen]);

  const closeMobileMenu = () => setMobileMenuOpen(false);

  const handleSectionClick = (e, href) => {
    e.preventDefault();
    const id = href.replace('#', '');
    closeMobileMenu();
    if (location.pathname !== '/') {
      navigate('/');
      setTimeout(() => {
        const el = document.getElementById(id);
        if (el) el.scrollIntoView({ behavior: 'smooth' });
      }, 120);
      return;
    }
    const target = document.getElementById(id);
    if (target) target.scrollIntoView({ behavior: 'smooth' });
  };

  const scrollToTop = (e) => {
    if (location.pathname === '/') {
      e.preventDefault();
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
    closeMobileMenu();
  };

  return (
    <>
      <nav
        className={`fixed top-0 left-0 w-full z-50 transition-all duration-300 ${
          navSolid
            ? 'bg-black/85 backdrop-blur-xl border-b border-white/[0.08] py-3.5'
            : 'bg-gradient-to-b from-black/80 to-transparent py-5'
        }`}
      >
        <div className="w-full px-5 sm:px-10 lg:px-16 xl:px-24 flex items-center justify-between gap-6">
          {/* Brand Wordmark with Cinema Red MX Mark */}
          <Link
            to="/"
            onClick={scrollToTop}
            className="flex items-center gap-2.5 no-underline shrink-0 group"
          >
            <span className="w-7 h-7 rounded bg-[#E50914] flex items-center justify-center text-white text-[12px] font-bold tracking-tight shadow-[0_0_16px_rgba(229,9,20,0.45)]">
              MX
            </span>
            <span className="text-white text-[17px] font-semibold tracking-[-0.03em]">
              MotionX
            </span>
            <span className="hidden sm:inline-block h-3 w-px bg-white/20" />
            <span className="hidden sm:inline-block text-[11px] uppercase tracking-[0.14em] text-white/55">
              Studios
            </span>
          </Link>

          {/* Center Editorial Nav Links */}
          <div className="hidden lg:flex items-center gap-7">
            {NAV_ITEMS.map((item) => (
              <a
                key={item.label}
                href={item.href}
                onClick={(e) => handleSectionClick(e, item.href)}
                className="whitespace-nowrap text-[13px] font-normal tracking-[-0.01em] text-white/75 hover:text-[#E50914] transition-colors no-underline"
              >
                {item.label}
              </a>
            ))}
            <Link
              to="/case-studies/stadium-vfx"
              className="whitespace-nowrap text-[13px] font-normal tracking-[-0.01em] text-white/75 hover:text-[#E50914] transition-colors no-underline"
            >
              Case Study
            </Link>
          </div>

          {/* Right Cinema Red Pill CTA */}
          <div className="hidden sm:flex items-center gap-3 shrink-0">
            <a
              href={CALENDLY_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="whitespace-nowrap bg-[#E50914] hover:bg-[#ff1a25] text-white text-[13px] font-medium tracking-[-0.01em] px-5 py-2 rounded-full transition-all shadow-[0_0_24px_rgba(229,9,20,0.35)] no-underline"
            >
              Book a Call
            </a>
          </div>

          {/* Mobile Menu Trigger */}
          <button
            type="button"
            onClick={() => setMobileMenuOpen(true)}
            className="lg:hidden px-3.5 py-1.5 rounded-full bg-white/10 border border-white/15 backdrop-blur-md text-white text-[12px] font-medium cursor-pointer"
            aria-label="Open menu"
          >
            Menu
          </button>
        </div>
      </nav>

      {/* Mobile Drawer */}
      <AnimatePresence>
        {mobileMenuOpen && (
          <motion.div
            className="fixed inset-0 z-[110] bg-black/95 backdrop-blur-2xl flex flex-col justify-between p-6 sm:p-10 overflow-y-auto"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
          >
            <div className="flex items-center justify-between border-b border-white/10 pb-4">
              <div className="flex items-center gap-2.5">
                <span className="w-7 h-7 rounded bg-[#E50914] flex items-center justify-center text-white text-xs font-bold">
                  MX
                </span>
                <span className="text-white text-[17px] font-semibold tracking-[-0.03em]">
                  MotionX Studios
                </span>
              </div>
              <button
                type="button"
                onClick={closeMobileMenu}
                className="w-9 h-9 rounded-full bg-white/10 flex items-center justify-center text-white text-sm cursor-pointer"
                aria-label="Close menu"
              >
                ✕
              </button>
            </div>

            <div className="my-8 flex flex-col gap-4">
              {NAV_ITEMS.map((link) => (
                <a
                  key={link.label}
                  href={link.href}
                  onClick={(e) => handleSectionClick(e, link.href)}
                  className="text-3xl font-normal tracking-[-0.03em] text-white hover:text-[#E50914] transition-colors no-underline"
                >
                  {link.label}
                </a>
              ))}
              <Link
                to="/case-studies/stadium-vfx"
                onClick={closeMobileMenu}
                className="text-2xl font-normal tracking-[-0.03em] text-[#E50914] hover:text-white transition-colors no-underline pt-4 border-t border-white/10"
              >
                Stadium VFX Case Study →
              </Link>
            </div>

            <a
              href={CALENDLY_URL}
              target="_blank"
              rel="noopener noreferrer"
              onClick={closeMobileMenu}
              className="w-full py-3.5 bg-[#E50914] text-white text-sm font-medium text-center rounded-full no-underline"
            >
              Book a Call
            </a>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default Navigation;
